import { Expose } from 'class-transformer';

import { SnModel } from '@lib/common/models';

import { SnStreamSources } from './stream-sources';

export class SnDVRStream extends SnModel {
  /**
   * Example: /playlist_dvr_timeshift-<shift>-<depth>.m3u8
   * Where <shift> is a value of time shift in seconds and <depth> is an optional parameter setting possible length which a viewer can rewind.
   * When <depth> is not provided, the default will return maximum and it's usually very large depending on the recording setting.
   * https://blog.wmspanel.com/2016/03/timeshifted-dvr-streaming-nimble.html.
   * Maximum timeShift scrubbing <depth> in seconds (2 hours * 60 minutes * 60 seconds = 7200 seconds).
   * This is intentionally set to 2 hours because when the user scrubs to a new location, a new playlist is created.
   */
  private static readonly MAX_SCRUBBING_DEPTH_SECONDS = 7200;

  @Expose()
  public management: string;

  @Expose()
  public video: string;

  @Expose()
  public videoFmp4: string;

  @Expose()
  public dvrRangeFmp4: string;

  @Expose()
  public timeshiftFmp4: string;

  @Expose()
  public wssRtsp: string;

  @Expose()
  public boschSession: string;

  @Expose()
  public boschTracksRetrieve: string;

  @Expose()
  public boschPlaybackSeek: string;

  @Expose()
  public boschPlaybackReplayStart: string;

  @Expose()
  public boschMessage: string;

  @Expose()
  public boschTimezoneRetrieve: string;

  @Expose()
  public export: string;

  @Expose({ name: 'mp4thumbnail' })
  public mp4thumbnail: string;

  @Expose()
  public latestMp4thumbnail: string;

  @Expose()
  public headers: any;

  @Expose()
  public timeline: any;

  constructor(data: any) {
    super();
    this.fromJSON(data);
  }

  public toStreamSources(timeShift?: number, start?: Date, end?: Date): SnStreamSources {
    if (!this.video) {
      return;
    }
    if (timeShift) {
      const timeShiftedUrl = this.video
        .split('.m3u8')
        .join(`_timeshift${Math.round(timeShift)}-${SnDVRStream.MAX_SCRUBBING_DEPTH_SECONDS}.m3u8`);

      return new SnStreamSources({ hls: timeShiftedUrl });
    }

    if (start && end) {
      const durationInSeconds = Math.floor(Math.abs(end.getTime() - start.getTime()) / 1000);
      const startInSeconds = Math.floor(start.getTime() / 1000);
      const rangeUrl = this.video
        .split('.m3u8')
        .join(`_range-${startInSeconds}-${durationInSeconds}_offset-0.m3u8`);
      return new SnStreamSources({ hls: rangeUrl });
    }

    return new SnStreamSources({ hls: this.video });
  }

  public toStreamSourcesFmp4(timeShift?: number, start?: Date, end?: Date): SnStreamSources {
    if (timeShift) {
      const timeShiftedUrl = this.timeshiftFmp4.replace(
        '<shift>-<depth>',
        `${Math.abs(Math.round(timeShift))}-${SnDVRStream.MAX_SCRUBBING_DEPTH_SECONDS}`,
      );
      return new SnStreamSources({ hls: timeShiftedUrl });
    }
    if (start && end) {
      const durationInSeconds = Math.floor(Math.abs(end.getTime() - start.getTime()) / 1000);
      const startInSeconds = Math.floor(start.getTime() / 1000);
      const rangeUrl = this.dvrRangeFmp4.replace(
        '<utc>-<duration>',
        `${startInSeconds}-${durationInSeconds}`,
      );
      return new SnStreamSources({ hls: rangeUrl });
    }
    return new SnStreamSources({ hls: this.videoFmp4 });
  }

  public toFususStreamSources(path: string, start: Date): SnStreamSources {
    const url = this.video.replace('/<file_path>', path).replace('<start_timestamp>', '0');
    return new SnStreamSources({ hls: url });
  }

  public toAvigilonStreamSources(start: Date): SnStreamSources {
    const urlFields = this.video.split('&t=live');
    const timeShift = start.toISOString();
    const timeShiftedUrl = `${urlFields[0]}&t=${timeShift}${urlFields[1]}`;
    return new SnStreamSources({ fmp4: timeShiftedUrl });
  }

  public getMp4Thumbnails(timeShift?: number): any {
    if (!this.mp4thumbnail) {
      return;
    }
    return this.mp4thumbnail.split('<utc>.mp4').join(`${timeShift}.mp4`);
  }

  public toFederalStreamSources(start: Date, end: Date): SnStreamSources {
    if (!this.video) {
      return;
    }
    const startInSeconds = Math.floor(start.getTime() / 1000);
    const endInSeconds = Math.floor(end.getTime() / 1000);
    const videoUrl = this.video
      .replace('<start_timestamp>', startInSeconds.toString())
      .replace('<end_timestamp>', endInSeconds.toString());
    return new SnStreamSources({ hls: videoUrl });
  }

  public getFederalExportUrl(start: Date, end: Date): string {
    if (!this.export) {
      return;
    }
    const startInSeconds = Math.floor(start.getTime() / 1000);
    const endInSeconds = Math.floor(end.getTime() / 1000);
    return this.export
      .replace('<start_timestamp>', startInSeconds.toString())
      .replace('<end_timestamp>', endInSeconds.toString());
  }
}
